import { RefObject, useEffect } from 'react'
import { isTopmostOverlay } from './overlayStack'

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Keyboard focus for a ds2 overlay panel (one marked `data-ds2-overlay`) while `open`: focus moves into
 * the panel when it opens, Tab and Shift+Tab wrap inside it instead of walking out onto the page behind,
 * and focus goes back to whatever had it once the panel closes. Only the topmost overlay keeps focus, so
 * a Drawer or Modal opened on top of this panel takes the keyboard until it closes.
 */
export const useOverlayFocusTrap = (panelRef: RefObject<HTMLElement>, open: boolean): void => {
    useEffect(() => {
        if (!open) return
        const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const focusFrame = requestAnimationFrame(() => panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus())

        const handler = (e: KeyboardEvent) => {
            if (e.key !== 'Tab' || !panelRef.current || !isTopmostOverlay(panelRef.current)) return
            // A hidden or inert panel (a tab that is not shown) still matches the selector, but Tab never lands
            // there. Counting it would make its last control the trap's "last", so Tab from the real last
            // control would leave the panel.
            const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => !element.closest('[hidden], [inert]'))
            if (!focusable.length) return
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            const outside = !panelRef.current.contains(document.activeElement)
            if (e.shiftKey && (document.activeElement === first || outside)) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && (document.activeElement === last || outside)) {
                e.preventDefault()
                first.focus()
            }
        }
        document.addEventListener('keydown', handler)
        return () => {
            cancelAnimationFrame(focusFrame)
            document.removeEventListener('keydown', handler)
            previouslyFocused?.focus()
        }
    }, [open, panelRef])
}
